/**
 * Closure screen: numbered next steps, crisis line, confidentiality (Figma step 07).
 * @agentic members
 */

import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  ThemeIcon,
  Button,
} from "@mantine/core";

type NextStepsScreenProps = {
  patientDisplayName: string;
  careTeamPhoneDisplay?: string;
  /** True when the respondent is a caregiver reporting on a child (e.g. PSC-17). */
  caregiverReportMode?: boolean;
  onBack?: () => void;
};

const firstName = (displayName: string): string => {
  const part = displayName.trim().split(/\s+/)[0];
  return part ?? displayName;
};

/**
 * Final numbered next steps and crisis resources.
 * @agentic members
 */
export const NextStepsScreen = ({
  patientDisplayName,
  careTeamPhoneDisplay,
  caregiverReportMode = false,
  onBack,
}: NextStepsScreenProps) => {
  const fn = firstName(patientDisplayName);
  const resultsDestinationText = caregiverReportMode
    ? "We will send your child's results to their care team, and may share them with their primary care provider (PCP) to coordinate their care."
    : "We will send your results to your care team, and may share them with your primary care provider (PCP) to coordinate your care.";

  return (
    <Paper radius="lg" p="xl" shadow="sm">
      <Stack gap="lg">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" color="green" variant="light">
            ✓
          </ThemeIcon>
          <Title order={2} ta="center" c="#391640">
            {`All done, ${fn}`}
          </Title>
        </Stack>

        <Text
          size="xs"
          tt="uppercase"
          fw={600}
          style={{ letterSpacing: "0.04em", opacity: 0.35, color: "#2a1a2e" }}
        >
          What happens next
        </Text>

        <Stack gap="md">
          <NumberedStep n={1} text={resultsDestinationText} />
          <NumberedStep
            n={2}
            text="You will get your next check-in in about 6 weeks."
          />
          <NumberedStep
            n={3}
            text={
              careTeamPhoneDisplay
                ? `If you have any questions, text your care team at ${careTeamPhoneDisplay}.`
                : "If you have any questions about your scores, reach out to your care team."
            }
          />
        </Stack>

        {onBack && (
          <Button variant="subtle" size="md" fullWidth onClick={onBack}>
            ← Review your scores
          </Button>
        )}

        <Paper p="md" radius="md" bg="gray.0" withBorder>
          <Text size="sm">
            If you need immediate support, contact the 988 Suicide &amp; Crisis
            Lifeline by calling or texting{" "}
            <Text span fw={700} component="span">
              988
            </Text>
            .
          </Text>
        </Paper>

        <Text size="xs" ta="center" style={{ opacity: 0.3, color: "#2a1a2e" }}>
          Benchmark Health · Your responses are confidential
        </Text>
      </Stack>
    </Paper>
  );
};

const NumberedStep = ({ n, text }: { n: number; text: string }) => (
  <Group align="flex-start" gap="sm" wrap="nowrap">
    <ThemeIcon size={24} radius="xl" color="plum" variant="light">
      <Text size="xs" fw={700}>
        {n}
      </Text>
    </ThemeIcon>
    <Text size="sm" c="#2a1a2e" style={{ flex: 1 }}>
      {text}
    </Text>
  </Group>
);
